import dayjs from "dayjs";
import { ReactElement, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { DynamicFormRefValues } from "../../../../../../components/dynamicFormRef/types";
import { useRefs } from "../../../../../../hooks/useReactHookModified";
import { questionnaire } from "../../../../../../utils/constants/callService";
import { RequestErrors, Types } from "../../../../../../utils/enums";
import { handleAllErrors } from "../../../../../../utils/methods/handleErrors";
import { OnlyKeepTwoDecimals, roundTo } from "../../../../../../utils/methods/removeDecimals";
import { removeAllReference } from "../../../../../../utils/methods/removedReference";
import { Item, ObjectGeneric } from "../../../../../../utils/types/generals";
import { OptionActionRef, OptionActionSavePayload } from "../../../../components/optionActions/types";
import { useQuestionnaireContext } from "../../../../context";
import { SectionRef } from "../../fragments/section/contextProvider/provider/types";
import { ModalState, PermissionsData } from "../../typing/types";
import { QuestionnaireContext } from "../context";
import { compareSection, configActionPlan, formatCommentConfigResponse, formatQuestionnaire, formatSection, removeActionPlan } from "./methods";
import { CreateQuestionnaire, CreateQuestionnairePayload, OptionGroupItem, SectionError, SectionList, UpdataQuestionList, UpdataSecctionData } from "./types";
import { Template } from "../../../../../../utils/enums/template";

const QuestionnaireProvider = ({ children }: { children: ReactElement }) => {
    const { state, setState } = useQuestionnaireContext()
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const { id } = useParams()
    // const state: any = location?.state?.id == id ? location?.state : {}
    const refOptions = useRefs<OptionActionRef>()
    const sectionRef = useRefs<SectionRef>()
    const [optionGroup, setOptionGroup] = useState<Array<OptionGroupItem>>([])
    const [options, setOptions] = useState<Array<Item>>([])
    const [sectionError] = useState<SectionError[]>([])
    const [dataHeader, setDataHeader] = useState<DynamicFormRefValues | undefined>({})
    const [totalSectionPoints, setTotalSectionPoints] = useState<Record<string, number>>({})
    const [totalPoints, setTotalPoints] = useState(Number(0).toFixed(2))
    const [questionTotal, setTotalQuestion] = useState(0)
    const [generalconfig, setGeneralconfig] = useState<OptionActionSavePayload>()
    const [openConfig, setOpenConfig] = useState('')
    const [isSectionConfig, setIsSectionConfig] = useState(false)
    const [snackbar, setSnackbar] = useState({
        open: false,
        type: Types.SUCCESS,
        message: ''
    })
    const [loadings, setLoadings] = useState({
        main: false,
        modal: false,
        edit: false
    })
    const [averagePoints, setAverage] = useState(Number(totalPoints ?? 0))
    const [headerConfig, setHeaderConfig] = useState({
        created: '',
        date: dayjs()
    })
    const [idOfTheSectionsIsEditing, setIdSectionIsEditing] = useState('')
    const [modal, setModal] = useState<ModalState>({ open: false, title: '', description: '' })
    const [sectionList, setSectionList] = useState<SectionList>([{ id: uuidv4() }])
    const [questionnaireCopy, setQuestionnaireCopy] = useState<{ section: SectionList; header?: DynamicFormRefValues; config?: OptionActionSavePayload }>()
    const [loading, setLoading] = useState({
        modal: false
    })
    const isGeneral = dataHeader?.option == '0'
    const closeSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }))
    }
    const closeModal = () => {
        setModal({ open: false, title: '', description: '' })
    }
    const handleTotalPoints = ({ totalPoints }: { totalPoints: number; addToAverage?: boolean }) => {
        setTotalPoints(String(totalPoints))
    }
    const getQuestionnaireData = async () => {
        if ((state?.id && state?.id == id)) {
            setIsSectionConfig(Boolean(state?.dataHeader?.isSectionConfig))
            setDataHeader(state?.dataHeader)
            setSectionList(state?.sectionList)
            setQuestionnaireCopy(state.copy)
            setAverage(OnlyKeepTwoDecimals({ value: state?.averagePoints }))
            setGeneralconfig(state?.generalconfig)
            let totaslSectionPoints = {}

            state?.sectionList?.forEach((section) => {
                totaslSectionPoints = {
                    ...totaslSectionPoints,
                    [section?.id ?? '']: section?.averageSection
                }
            })
            setTotalSectionPoints(totaslSectionPoints)
        } else {
            if (!id) {
                navigate('/dashboard/questionnarie/main')
                return;
            }

            setLoadings(prev => ({ ...prev, main: true }))
            const response = await questionnaire.getQuestionarieById(id)/* testUat */
            setHeaderConfig({
                created: `${response?.userCreator?.firstName} ${response?.userCreator?.lastName}`,
                date: dayjs(response?.createTime)
            })
            setIsSectionConfig(response?.scoreBySection)
            const header = {
                parentId: response?.parentId || id,
                version: response?.version,
                "quest_name": response?.name ?? "",
                "description": response?.description ?? "",
                "instruction": response?.instruction ?? "",
                statusId: response.statusId,
                ...!!response?.areaId && {
                    "areas": {
                        label: response?.area?.name ?? "",
                        value: response?.areaId,
                        id: response?.areaId
                    }
                },
                ...!!response?.departmentId && {
                    "departments": {
                        label: response?.department?.name ?? "",
                        value: response?.departmentId,
                        id: response?.departmentId
                    }
                },
                "totalPoints": response?.totalPoints,
                "option": Boolean(response?.actionPlanByItem) ? '1' : '0'
            }

            setDataHeader(() => ({ ...header as DynamicFormRefValues }))

            const sectionList = response.questionnaireSections?.map(section => formatSection({ section, t }))
            const questionPointsPoint = response.questionnaireSections.flatMap((section) => ({
                points: section.steps.flatMap((step) => (
                    step.question.questionOptions.reduce((counter, option) => counter + Number(option?.correctOption ? option.score : 0), 0)
                )),
                id: section.id
            }))
            const questionAmmount = questionPointsPoint.reduce((counter, question) => counter + question.points.length ?? 0, 0)
            const sectionPoint = questionPointsPoint.map(section => ({
                ...section,
                points: roundTo(section.points.reduce((counter, question) => counter + question, 0),2) 
            }))

            const totalPoints = OnlyKeepTwoDecimals({ value: Number(sectionPoint.reduce((counter, section) => counter + section.points, 0)) })
            setTotalPoints(String(totalPoints ?? 0))
            setAverage(() => Number(totalPoints ?? 0))
            const generalConfig = {
                ...response?.questionaireActionPlan?.[0]?.actionPlan && { actionPlan: configActionPlan({ actionPlan: response.questionaireActionPlan?.[0]?.actionPlan, t }) },
                ...response?.questionnaireCommentConfig && { comment: formatCommentConfigResponse({ config: response?.questionnaireCommentConfig }) },
                ...response?.questionnaireSignsConfig.length!==0 && { reportSignature: response?.questionnaireSignsConfig.map(sign => ({
                    id: sign.id,
                    requeired: Boolean(sign.required),
                    description: sign.description ?? '',
                    addComment: Boolean(sign.comment),
                    reqiredComment: Boolean(sign.requiredComment)
                })) }
            }
            setGeneralconfig(generalConfig)

            if (!Boolean(response.questionnaireSections.length)) {
                setLoadings(prev => ({ ...prev, main: false }))
            } else {
                let sectionPoints = {}
                for (let section of sectionPoint) {
                    sectionPoints = { ...sectionPoints, [section.id]: section.points }
                }
                setTotalSectionPoints(sectionPoints)
                const transformedSection = sectionList.map(section => ({
                    ...section,
                    averageSection: sectionPoint.find(sectionPoint => String(sectionPoint.id) == String(section.id))?.points
                }))
                setSectionList(transformedSection)
                setQuestionnaireCopy({ section: removeAllReference(transformedSection), header: removeAllReference(header as DynamicFormRefValues), config: removeAllReference(generalConfig) })
            }
            setTotalQuestion(questionAmmount)
            setLoadings(prev => ({ ...prev, main: false }))

        }

        window.history.replaceState({}, '')
        navigate(`${location.pathname}${location.search ?? ''}`, { replace: true });
    }

    const addSection = () => {
        setSectionList(prev => [...prev, { id: uuidv4() }])
    }

    const removeSection = ({ id }: { id: string }) => {
        setModal(prev => ({
            ...prev,
            open: true,
            title: '¿Está seguro que desea eliminar esta sección?',
            description: 'Al eliminar la sección, se perderán las configuraciones realizadas',
            cancel: closeModal,
            confirm: () => {
                const totalSectionPoints = sectionRef.refsByKey[id]?.getTotalSectionPoints
                if (totalSectionPoints) {
                    const points = totalSectionPoints()
                    setSectionList(prev => prev.filter(section => section.id != id))
                    setTotalPoints(prev => OnlyKeepTwoDecimals({ value: (Number(prev) - Number(points)) }).toString())
                    setAverage(prev => OnlyKeepTwoDecimals({ value: (Number(prev) - Number(points)) }))
                    closeModal()
                }
            }
        }))
    }

    const orderSection = (list: any[]) => {
        setSectionList(list)
    }

    const validateSection = ({ data }: {
        data: ({
            sectionData?: DynamicFormRefValues | undefined;
            questionList: any[];
        } | null)[]
    }) => {
        if (!Boolean(data?.length)) return { code: 'NOT_SECTION' }
        const sectionErrors: SectionError[] = []
        let errorInSectionPoints = false
        const sections = sectionList.map(section => {
            const sectiionTotalPointIsDifferent = OnlyKeepTwoDecimals({ value: Number(section?.sectionDefault?.sectionPoints ?? 0) }) != OnlyKeepTwoDecimals({ value: Number(section?.averageSection ?? 0) })
            if (sectiionTotalPointIsDifferent && !errorInSectionPoints) {
                errorInSectionPoints = true
            }
            sectionErrors.push({
                id: section.id,
                totalPoints: sectiionTotalPointIsDifferent
            })
            const questionIdList = section?.questionListDefault?.map(question => ({ id: String(question.question?.id) })) || []
            return {
                ...section,
                questionListDefault: section?.questionListDefault?.map((question, index) => removeActionPlan({ question, questionIdList, questionIndex: index })) || []
            }
        })
        setSectionList(sections)
        return { sectionErrors, errorInSectionPoints }
    }

    const getDataOfTheSectionList = () => {
        const sectionListFiltered = sectionList.filter(section => Boolean(section?.sectionDefault?.sectionName) && Boolean(section?.questionListDefault?.length)).map(section => {
            const questionIdList = section?.questionListDefault?.map(question => ({ id: String(question.question?.id) })) || []
            return {
                sectionData: section?.sectionDefault,
                questionList: section?.questionListDefault?.map((question, index) => removeActionPlan({ question, questionIdList, questionIndex: index })) || [],
                averageSection: section.averageSection,
            }
        })

        return { sectionListFiltered, thereAreSectionWithoutQuestions: sectionListFiltered?.length < sectionList?.length }
    }
    const createQuestionnaire: CreateQuestionnaire = async (payload) => {
        let externalConfig: {
            data: OptionActionSavePayload;
            errorCount: number;
        } | undefined
        const getData = refOptions.refsByKey['general']?.getData
        if (!!payload?.save && getData) {
            externalConfig = getData()
        }
        const generalConfigLoacel = !payload?.save ? generalconfig : externalConfig?.data
        const isnotSameQuestionnaire = compareSection({
            copy: questionnaireCopy?.section,
            section: sectionList,
            header: dataHeader,
            headerCopy: questionnaireCopy?.header,
            config: generalConfigLoacel,
            configCopy: questionnaireCopy?.config
        })
        if (!isnotSameQuestionnaire) {
            setModal(() => ({
                open: true,
                description: '',
                title: t('general.thereNotChange'),
                confirm: closeModal
            }))
            return true
        }
        const existGeneralConnfig = !isGeneral || (isGeneral && generalConfigLoacel?.actionPlan)
        if ((!idOfTheSectionsIsEditing && existGeneralConnfig) || payload?.ignoreEdit) {
            const { sectionListFiltered: data, thereAreSectionWithoutQuestions } = getDataOfTheSectionList()

            const response = validateSection({ data })
            let headerMessage = (Number(averagePoints) != Number(dataHeader?.totalPoints ?? '0') && !isSectionConfig && !payload?.draft) ? 'los puntos totales no coinciden' : ''
            if (!payload?.ignoreEdit && !!isSectionConfig && response.errorInSectionPoints) {
                headerMessage = 'Hay secciones en las que la suma no coincide con el total esperado.'
            }
            const message = `${response?.code ? handleAllErrors(response?.code as RequestErrors, t) : ''} ${headerMessage}`.trim()
            if (Boolean(message)) {
                setModal(prev => ({
                    ...prev,
                    open: true,
                    title: 'Advertencia',
                    description: capitalizeFirstLetter(message.toLowerCase()),
                    confirm: () => {
                        closeModal()
                    }
                }))
            } else {
                if (payload?.ignoreEdit) {
                    submit({ data, generalconfig: generalConfigLoacel, draft: payload?.draft })
                } else {
                    setModal(() => ({
                        open: true,
                        description: thereAreSectionWithoutQuestions ?
                            'Hay secciones sin preguntas configuradas que no se guardarán. ¿Deseas guardar la configuración actual?' :
                            '¿Está seguro que desea guardar el cuestionario con la configuración actual?',
                        title: thereAreSectionWithoutQuestions ? 'Advertencia' : t('Questionarie.modal.permission.update'),
                        cancel: closeModal,
                        confirm: async () => {

                            submit({ data, save: payload?.save, generalconfig: generalConfigLoacel, draft: payload?.draft })
                        }
                    }))
                }
            }
        } else {
            setModal(prev => ({
                ...prev,
                open: true,
                title: !existGeneralConnfig ? 'No se configuró el plan de acción' : 'Hay secciones con preguntas con ediciones sin guardar',
                description: !existGeneralConnfig ? '' : '¿Quiere guardar de todas maneras?',
                confirm: () => {
                    createQuestionnaire({ ignoreEdit: true })
                    closeModal()
                },
                cancel: !existGeneralConnfig ? undefined : closeModal
            }))
        }
    }
    const submitPermissions = async (data?: { response?: boolean; payload: PermissionsData | undefined }) => {

        let news: ObjectGeneric[] = []
        let deletes: string[] = []
        setLoadings(prev => ({ ...prev, modal: true }))

        const modelPermissions = ({ key, keyCheck, value }: { key: string; keyCheck: string; value: { [x: string]: boolean } }) => {
            return {
                access: value?.[`access${keyCheck}`] ? 1 : 0,
                report: value?.[`report${keyCheck}`] ? 1 : 0,
                result: value?.[`result${keyCheck}`] ? 1 : 0,
                emailNotification: value?.[`emailNotification${keyCheck}`] ? 1 : 0,
                userId: key,
            }
        }
        const ckeckUsers = data?.payload?.checksUser
        setLoadings(prev => ({ ...prev, modal: true }))

        Object.entries(data?.payload?.users ?? {}).forEach(([key, item]) => {
            if (!!item && Object.values(ckeckUsers?.[key] ?? {})?.find((itemPermissions) => !!itemPermissions) && (data?.payload?.usersOld[key] === undefined || !!data?.payload?.usersOld[key])) {
                news.push({
                    id: key,
                    questionnaireMain: modelPermissions({ keyCheck: 'Q', key, value: ckeckUsers?.[key] }),
                    reportFolio: modelPermissions({ keyCheck: 'F', key, value: ckeckUsers?.[key] })
                })
            }
        })

        Object.entries(data?.payload?.usersOld ?? {}).forEach(([key, item]) => {
            if (!item) {
                deletes.push(key)
            }
        })

        const permissionsFormated = {
            modelId: id,
            news,
            deletes
        }

        const response = await questionnaire.createPermissions(permissionsFormated)
        setLoadings(prev => ({ ...prev, modal: false }))
        if (!response.errors) {
            if (!!data?.response) {
                const typeResponse = !['SUCCESS', 'SUCCESS_REMOVE_PERMISSIONS'].includes(response.code)

                setSnackbar({
                    open: true,
                    message: t(`Questionarie.manageUser.submit.snackbar.update.${typeResponse ? 'error' : 'success'}`),
                    type: typeResponse ? Types.ERROR : Types.SUCCESS,
                })
            }
            return true
        } else {
            return response
        }
    }
    const submit = async ({ draft, data, generalconfig, confirm }: {
        data: ({
            sectionData?: DynamicFormRefValues | undefined;
            questionList: any[];
        } | null)[]
        save?: boolean
        draft?: boolean
        generalconfig?: OptionActionSavePayload
        confirm?: (payload: { id: string | number }) => void
        created?: CreateQuestionnairePayload['created']
    }) => {
        let payload: {
            data: OptionActionSavePayload;
            errorCount: number;
        } | undefined
        const getData = refOptions.refsByKey['general']?.getData
        if (Boolean(openConfig) && getData) {
            payload = getData()
            setGeneralconfig(payload.data)
        }

        if (!payload?.errorCount) {
            const body = await formatQuestionnaire({ generalconfig, data: data as any, isGeneral, t, dataHeader, isSectionConfig })

            setLoadings(prev => ({ ...prev, modal: true }))
            let response: any
            if (id) {
                response = await questionnaire.updateQuestionnarie(id, body)

                if (!response?.errors) {
                    setDataHeader(prev => ({
                        ...prev,
                        statusId: response.statusId
                    }))
                    setModal({
                        open: true,
                        title: t(`Questionarie.modal.submit.success.title`),
                        description: t(`Questionarie.modal.submit.success.description`),
                        confirm: confirm ? () => { confirm({ id: response?.id }) } : closeModal
                    })
                    setOpenConfig('')
                    navigate(`/dashboard/questionnarie/edit/${response?.id}`)
                    setQuestionnaireCopy({
                        section: removeAllReference(sectionList),
                        config: removeAllReference(generalconfig),
                        header: removeAllReference(dataHeader),
                    })
                } else {
                    setModal({
                        open: true,
                        title: t(`Questionarie.modal.submit.error.title`),
                        description: '',
                        cancel: closeModal
                    })
                }
            }
            setLoadings(prev => ({ ...prev, modal: false, edit: false }))
        }
    }
    function capitalizeFirstLetter(text: string) {
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    const sectionAmount = ({ id, questionAmount }: { id: string; questionAmount: number }) => {
        /* const foundSectionIndex = sectionList.findIndex(section => section.id == id)
        sectionList[foundSectionIndex].totalQuestions = questionAmount
        setTotalQuestion(sectionList.reduce((count, section) => count + Number(section.totalQuestions ?? 0), 0))
        setSectionList(sectionList) */
        setSectionList(prev => {
            const newSectionList = prev.map(section => {
                if (section.id === id) {
                    return {
                        ...section,
                        totalQuestions: questionAmount,
                    }
                }
                return section
            })

            setTotalQuestion(
                newSectionList.reduce((count, section) => count + Number(section.totalQuestions ?? 0), 0)
            )

            return newSectionList
        })
    }

    const sectionTotalPoints = ({ id, total }: { id: string; total: number }) => {
        const foundSectionIndex = sectionList.findIndex(section => section.id == id)
        sectionList[foundSectionIndex].averageSection = total
        setAverage(OnlyKeepTwoDecimals({ value: sectionList.reduce((count, section) => count + Number(section.averageSection ?? 0), 0) }))
        setSectionList(sectionList)
    }

    const getGroupTypes = async () => {
        const response = await questionnaire.getQuestionType()
        let optionGroupItems: Item[] | undefined
        if (!response?.errors) {
            optionGroupItems = response?.map(item => ({
                label: item.description ?? "",
                value: item.id.toString() ?? "",
                id: item.id?.toString()
            }))
            setOptions(optionGroupItems)
        }
    }

    const getOptionGroup = async (type:Template) => {
        const response = await questionnaire.getOptionGroup(type)
        let optionGroupItems: OptionGroupItem[] | undefined
        if (!response?.errors) {
            optionGroupItems = response?.map(item => ({
                label: item.title ?? "",
                value: item.id.toString() ?? "",
                id: item.id?.toString(),
                typeOptionGroupId: item.typeOptionGroupId,
                options: item.options,
            }))
            setOptionGroup(optionGroupItems)
        }
    }

    const openGeneralConfig = () => {
        refOptions.refsByKey?.['general']?.fillData({ defaultData: generalconfig })
        setOpenConfig('general')
    }

    const saveGeneralConfig = (payload: OptionActionSavePayload) => {
        setGeneralconfig(payload)
        setOpenConfig('')
    }

    const handleNavigate = () => {
        const stateLoacel = {
            sectionList,
            dataHeader: { ...dataHeader, isSectionConfig },
            generalconfig,
            averagePoints,
            id,
            origin: 'questionnaire',
            copy: questionnaireCopy
        }
        const isnotSameQuestionnaire = compareSection({
            copy: questionnaireCopy?.section,
            section: sectionList,
            header: dataHeader,
            headerCopy: questionnaireCopy?.header,
            config: generalconfig,
            configCopy: questionnaireCopy?.config
        })
        if (!isnotSameQuestionnaire) {
            navigate(`/dashboard/questionnarie/header/${id}`)
        }
        setModal((prev) => ({
            ...prev,
            open: true,
            description: '',
            title: t('Questionarie.saveQuestionnairesChanges'),
            confirmText: t('general.save'),
            cancelText: t('general.continue'),
            close: () => {
                closeModal()
            },
            cancel: () => {
                setState(stateLoacel)
                navigate(`/dashboard/questionnarie/header/${id}`)
            },
            confirm: () => {
                setLoadings(prev => ({ ...prev, edit: true }))
                const { sectionListFiltered: data } = getDataOfTheSectionList()
                setSnackbar({
                    open: true,
                    type: Types.INFO,
                    message: 'Guardando los cambios...'
                })
                submit({
                    data,
                    generalconfig,
                    draft: true,
                    confirm: ({ id }) => {
                        closeModal()
                        setState(stateLoacel)
                        navigate(`/dashboard/questionnarie/header/${id}`)
                    }
                })
                closeModal()
            }
        }))


    }

    const updateQuestionList: UpdataQuestionList = ({ id, question, totalSectionPoints }) => {
        setSectionList(prev => {
            const newSectionList = prev.map(section => {
                if (section.id == id) {
                    return {
                        ...section,
                        ...(totalSectionPoints || totalSectionPoints == 0) && { averageSection: totalSectionPoints },
                        questionListDefault: question
                    }
                }
                return section
            })
            setAverage(OnlyKeepTwoDecimals({ value: newSectionList.reduce((count, section) => count + Number(section.averageSection ?? 0), 0) }))
            return newSectionList
        })
    }

    const updateSectionData: UpdataSecctionData = ({ data, id }) => {
        setSectionList(prev => {
            const newSectionList = prev.map(section => {
                if (section.id == id) {
                    return {
                        ...section,
                        sectionDefault: data
                    }
                }
                return section
            })
            setAverage(OnlyKeepTwoDecimals({ value: Number(newSectionList.reduce((count, section) => count + Number(section.averageSection ?? 0), 0)) }))
            return newSectionList
        })
    }

    useLayoutEffect(() => {
        getGroupTypes()
        getOptionGroup(Template.DEFAULT)
        getQuestionnaireData()
    }, [])

    return (
        <QuestionnaireContext.Provider
            value={{
                optionGroup,
                options,
                questionTotal,
                dataHeader,
                modal,
                loading,
                sectionList,
                getOptionGroup,
                sectionRef,
                totalPoints,
                isGeneral,
                headerConfig,
                averagePoints,
                openConfig,
                generalconfig,
                loadings,
                isSectionConfig,
                sectionError,
                setTotalSectionPoints,
                totalSectionPoints,
                refOptions: refOptions.addRef,
                snackbar,
                submitPermissions,
                updateSectionData,
                updateQuestionList,
                sectionTotalPoints,
                handleNavigate,
                saveGeneralConfig,
                openGeneralConfig,
                sectionAmount,
                closeSnackbar,
                setIdSectionIsEditing,
                orderSection,
                setTotalPoints,
                handleTotalPoints,
                createQuestionnaire,
                removeSection,
                addSection,
                setLoading,
                setModal,
                closeModal,
                setDataHeader,
                setOptionGroup,
                setOptions
            }}
        >
            {children}
        </QuestionnaireContext.Provider>
    )
}

export { QuestionnaireProvider };

